import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FileText, Search, Download, Copy, Edit, Trash2, History, LayoutTemplate, CheckCircle2, XCircle, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR, formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const STATUS_STYLES = {
  Draft: { bg: "#F3F4F6", color: "#374151" },
  Sent: { bg: "#DBEAFE", color: "#1E40AF" },
  Accepted: { bg: "#DCFCE7", color: "#166534" },
  Rejected: { bg: "#FEE2E2", color: "#991B1B" },
  Expired: { bg: "#FEF3C7", color: "#92400E" },
};

export default function Quotations() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = !["ACCOUNTS"].includes(user?.role);
  const canManageTemplates = ["ADMIN", "SALES_MANAGER"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (search) p.set("search", search);
    if (status) p.set("status", status);
    const { data } = await api.get(`/quotations?${p}`);
    setItems(data.items); setStats(data.stats); setLoading(false);
  }, [search, status]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (q) => {
    try {
      const res = await api.get(`/quotations/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${q.quotation_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download PDF"); }
  };

  const duplicate = async (q) => {
    const { data } = await api.post(`/quotations/${q.id}/duplicate`);
    toast.success(`Duplicated as ${data.quotation_number}`);
    load();
  };

  const doDelete = async () => {
    await api.delete(`/quotations/${confirm.id}`);
    toast.success("Quotation deleted"); setConfirm(null); load();
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Quotations</h1>
          <p className="mt-0.5 text-sm text-slate-500">{stats.total || 0} quotations · {formatINR(stats.total_value || 0)} total quoted</p>
        </div>
        <div className="flex items-center gap-2">
          {canManageTemplates && (
            <Button data-testid="manage-templates-btn" variant="outline" onClick={() => navigate("/quotation-templates")} className="gap-1.5"><LayoutTemplate className="h-4 w-4" /> Templates</Button>
          )}
          {canWrite && <Button data-testid="new-quotation-btn" onClick={() => navigate("/quotations/new")} className="gap-1.5 font-semibold"><Plus className="h-4 w-4" /> New Quotation</Button>}
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        <Kpi label="Total" value={stats.total || 0} icon={FileText} />
        <Kpi label="Draft" value={stats.draft || 0} icon={Edit} color="#6B7280" />
        <Kpi label="Sent" value={stats.sent || 0} icon={Send} color="#1E40AF" />
        <Kpi label="Accepted" value={stats.accepted || 0} icon={CheckCircle2} color="#166534" />
        <Kpi label="Rejected" value={stats.rejected || 0} icon={XCircle} color="#991B1B" />
        <Kpi label="Expired" value={stats.expired || 0} icon={Clock} color="#92400E" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="quotation-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by number, customer, or title" className="pl-9" />
        </div>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="quotation-status-filter" className="w-40"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.keys(STATUS_STYLES).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState testid="quotations-empty" icon={FileText} title="No quotations yet"
          description={canWrite ? "Create your first quotation using the Dazlac template." : "No quotations found."}
          action={canWrite ? <Button onClick={() => navigate("/quotations/new")} className="gap-1.5"><Plus className="h-4 w-4" /> New Quotation</Button> : null} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Number</th>
                <th className="px-4 py-3 text-left">Customer / Title</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((q) => (
                <tr key={q.id} data-testid={`quotation-row-${q.id}`} className="group border-t border-slate-100 hover:bg-slate-50/70">
                  <td className="px-4 py-3">
                    <button onClick={() => navigate(`/quotations/${q.id}`)} className="font-mono text-sm font-semibold text-[#0057A8] hover:underline">{q.quotation_number}</button>
                    {q.revision > 1 && <span className="ml-2 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">rev {q.revision}</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-800">{q.customer?.name || "—"}</div>
                    <div className="text-xs text-slate-400">{q.title}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(q.quotation_date)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-slate-800">{formatINR(q.grand_total)}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold" style={{ backgroundColor: STATUS_STYLES[q.status]?.bg, color: STATUS_STYLES[q.status]?.color }}>{q.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1 opacity-0 transition group-hover:opacity-100">
                      <button title="Download PDF" onClick={() => downloadPdf(q)} data-testid={`quot-pdf-${q.id}`} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Download className="h-4 w-4" /></button>
                      {canWrite && <button title="Edit" onClick={() => navigate(`/quotations/${q.id}`)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Edit className="h-4 w-4" /></button>}
                      {canWrite && <button title="Duplicate" onClick={() => duplicate(q)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Copy className="h-4 w-4" /></button>}
                      {["ADMIN", "SALES_MANAGER"].includes(user?.role) && <button title="Delete" onClick={() => setConfirm(q)} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog open={!!confirm} onOpenChange={() => setConfirm(null)}
        title="Delete quotation?" description={`${confirm?.quotation_number} will be permanently deleted along with all revisions.`}
        confirmText="Delete" onConfirm={doDelete} />
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color = "#0057A8" }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <div className="mt-1 text-2xl font-bold text-slate-900">{value}</div>
    </div>
  );
}
